const COMPARE_API_URL = 'https://rates-public.predictablebenefits.com/public/plans/medical/compare';
const COMPARE_CHUNK_SIZE = 75;

type PlanWithEmergency = {
    id: string;
    emergency_numerical?: number | null;
    emergency_room?: string | null;
};

async function fetchEmergencyRoomChunk(
    ids: string[],
): Promise<Array<{ id: string; emergency_room: string | null }>> {
    if (ids.length === 0) return [];

    const url = `${COMPARE_API_URL}?ids=${encodeURIComponent(ids.join(','))}`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
        throw new Error('Failed to enrich emergency benefits');
    }

    const data = await response.json();
    const plans: unknown[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.plans)
            ? data.plans
            : [];

    return plans
        .filter((plan): plan is Record<string, unknown> & { id: string } =>
            Boolean(plan && typeof plan === 'object' && typeof (plan as { id?: unknown }).id === 'string'),
        )
        .map((plan) => ({
            id: plan.id,
            emergency_room: typeof plan.emergency_room === 'string' ? plan.emergency_room : null,
        }));
}

/**
 * Search results only include `emergency_numerical`. When that is null
 * (coinsurance like "50% after deductible"), pull `emergency_room` from the
 * compare API so plan cards can match the plans detail page.
 */
export async function enrichPlansWithVisitBenefits<T extends PlanWithEmergency>(
    plans: T[],
): Promise<T[]> {
    if (!plans.length) return plans;

    const idsToEnrich = plans
        .filter((plan) => plan.emergency_numerical == null && !plan.emergency_room)
        .map((plan) => plan.id);
    if (idsToEnrich.length === 0) return plans;

    const chunks: string[][] = [];
    for (let i = 0; i < idsToEnrich.length; i += COMPARE_CHUNK_SIZE) {
        chunks.push(idsToEnrich.slice(i, i + COMPARE_CHUNK_SIZE));
    }

    const enrichedChunks = await Promise.all(
        chunks.map((chunk) => fetchEmergencyRoomChunk(chunk).catch(() => [])),
    );

    const byId = new Map<string, string | null>();
    for (const chunk of enrichedChunks) {
        for (const plan of chunk) {
            byId.set(plan.id, plan.emergency_room);
        }
    }

    if (byId.size === 0) return plans;

    return plans.map((plan) => {
        if (!byId.has(plan.id)) return plan;
        return {
            ...plan,
            emergency_room: byId.get(plan.id) ?? plan.emergency_room ?? null,
        };
    });
}
